import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from '@/app/store/authStore';
import { Loader2 } from 'lucide-react';

// Login form validation schema
const loginSchema = z.object({
    username: z.string().min(1, "Username is required"),
    password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

// Decorative dot pattern component
const DotPattern: React.FC<{ className?: string }> = ({ className = "" }) => (
    <div className={`grid grid-cols-5 gap-2 ${className}`}>
        {Array.from({ length: 25 }).map((_, i) => (
            <div
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-primary/40"
            />
        ))}
    </div>
);

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    // Only access what we need from the store
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const user = useAuthStore((state) => state.user);
    const loginAction = useAuthStore((state) => state.loginAction);
    const isLoading = useAuthStore((state) => state.isLoading);
    const error = useAuthStore((state) => state.error);
    const getDefaultRoute = useAuthStore((state) => state.getDefaultRoute);

    const { register, handleSubmit, formState: { errors } } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            username: "",
            password: ""
        }
    });

    const onSubmit = async (data: LoginFormValues): Promise<void> => {
        await loginAction(data);
    };

    // Redirect if already authenticated AND user profile with role is loaded
    // This ensures getDefaultRoute() has access to the user's role for correct routing
    useEffect(() => {
        if (isAuthenticated && user?.role) {
            const defaultRoute = getDefaultRoute();
            console.log('Navigating to', defaultRoute, 'for role:', user.role.name);
            navigate(defaultRoute, { replace: true });
        }
    }, [isAuthenticated, user, navigate, getDefaultRoute]);

    return (
        <div className="flex min-h-screen bg-background">
            {/* Left Panel - Login Form */}
            <div className="flex flex-1 items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md space-y-8 animate-in fade-in slide-in-from-left-4 duration-500">
                    {/* Logo */}
                    <div className="flex justify-center">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center shadow-diso-primary">
                                <span className="text-white font-bold text-lg">AE</span>
                            </div>
                            <span className="text-2xl font-semibold text-foreground tracking-tight">
                                AUTO ERP
                            </span>
                        </div>
                    </div>

                    {/* Welcome Text */}
                    <div className="text-center space-y-2">
                        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                            Welcome back
                        </h1>
                        <p className="text-muted-foreground">
                            Enter your credentials to access your account
                        </p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="space-y-4">
                            {/* Username Field */}
                            <div className="space-y-2">
                                <Label
                                    htmlFor="username"
                                    className="text-sm font-medium text-foreground"
                                >
                                    Username
                                </Label>
                                <Input
                                    id="username"
                                    type="text"
                                    placeholder="Enter your username"
                                    className="h-12 px-4 bg-background border-input rounded-xl transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    {...register("username")}
                                    aria-invalid={errors.username ? "true" : "false"}
                                />
                                {errors.username && (
                                    <p className="text-xs text-destructive mt-1">
                                        {errors.username.message}
                                    </p>
                                )}
                            </div>

                            {/* Password Field */}
                            <div className="space-y-2">
                                <Label
                                    htmlFor="password"
                                    className="text-sm font-medium text-foreground"
                                >
                                    Password
                                </Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="Enter your password"
                                    className="h-12 px-4 bg-background border-input rounded-xl transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    {...register("password")}
                                    aria-invalid={errors.password ? "true" : "false"}
                                />
                                {errors.password && (
                                    <p className="text-xs text-destructive mt-1">
                                        {errors.password.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20">
                                <p className="text-sm text-destructive">{error}</p>
                            </div>
                        )}

                        {/* Sign In Button */}
                        <Button
                            type="submit"
                            className="w-full h-12 rounded-xl bg-gradient-primary hover:opacity-90 shadow-diso-primary transition-all duration-200 text-base font-medium"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Signing In...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </Button>
                    </form>

                    {/* Footer */}
                    <p className="text-center text-sm text-muted-foreground">
                        Need help?{' '}
                        <span className="text-primary hover:underline cursor-pointer">
                            Contact Support
                        </span>
                    </p>
                </div>
            </div>

            {/* Right Panel - Branding (Hidden on mobile) */}
            <div className="hidden lg:flex flex-1 relative overflow-hidden">
                {/* Background with gradient */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(135deg, hsl(187 50% 54% / 0.06) 0%, hsl(187 50% 54% / 0.12) 100%)'
                    }}
                />

                {/* Decorative Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    {/* Top-left dots */}
                    <DotPattern className="absolute top-12 left-12" />

                    {/* Bottom-right dots */}
                    <DotPattern className="absolute bottom-12 right-12" />

                    {/* Abstract shapes */}
                    <div
                        className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-30"
                        style={{
                            background: 'radial-gradient(circle, hsl(187 50% 54% / 0.2) 0%, transparent 70%)'
                        }}
                    />
                    <div
                        className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-20"
                        style={{
                            background: 'radial-gradient(circle, hsl(187 50% 54% / 0.25) 0%, transparent 70%)'
                        }}
                    />
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center justify-center w-full p-12 animate-in fade-in slide-in-from-right-4 duration-500 delay-150">
                    {/* Welcome Message */}
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-semibold text-foreground mb-3">
                            Welcome to{' '}
                            <span className="text-primary">AUTO ERP</span>
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-md">
                            Manage your business operations efficiently with our comprehensive ERP solution
                        </p>
                    </div>

                    {/* Login Illustration */}
                    <img
                        src="/login-illustration.png"
                        alt="Business illustration"
                        className="max-w-lg w-full h-auto rounded-2xl shadow-diso-lg"
                    />

                    {/* Bottom tagline */}
                    <div className="mt-12 flex items-center gap-8 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                            <span>Sales & Invoicing</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                            <span>Inventory</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                            <span>Accounting</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
